/**
 * Regras da árvore da biblioteca usadas ao mover itens (arrastar e "Mover para…").
 * O servidor confere de novo; aqui é para só oferecer destinos válidos.
 */
import type { LibraryItem } from '../shared.js';

/** Itens que aparecem na árvore e podem trocar de pasta (as tabelas e as macros da mesa ficam fora). */
export const isMovable = (item: LibraryItem) => item.kind === 'folder' || item.kind === 'sheet' || item.kind === 'scene';

/** O item e tudo que está dentro dele. */
export function subtreeIds(library: readonly LibraryItem[], itemId: string): Set<string> {
  const ids = new Set([itemId]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const i of library) {
      if (i.parentId && ids.has(i.parentId) && !ids.has(i.id)) {
        ids.add(i.id);
        grew = true;
      }
    }
  }
  return ids;
}

/** Pode ir para `parentId` (null = raiz)? Não pode ir para onde já está, nem para dentro de si. */
export function canMoveInto(library: readonly LibraryItem[], item: LibraryItem, parentId: string | null): boolean {
  if (!isMovable(item) || item.parentId === parentId) return false;
  if (parentId === null) return true;
  const parent = library.find((i) => i.id === parentId);
  return !!parent && parent.kind === 'folder' && !subtreeIds(library, item.id).has(parentId);
}

/** Caminho de pastas até o item, para mostrar no menu ("Mapas / Antigos"). */
export function folderPath(library: readonly LibraryItem[], folderId: string): string {
  const names: string[] = [];
  const byId = new Map(library.map((i) => [i.id, i]));
  for (let at = byId.get(folderId); at && names.length < 32; at = at.parentId ? byId.get(at.parentId) : undefined) names.unshift(at.name);
  return names.join(' / ');
}

/** Pastas para onde o item pode ir, em ordem de caminho. */
export function moveTargets(library: readonly LibraryItem[], item: LibraryItem): { id: string; path: string }[] {
  return library
    .filter((i) => i.kind === 'folder' && canMoveInto(library, item, i.id))
    .map((i) => ({ id: i.id, path: folderPath(library, i.id) }))
    .sort((a, b) => a.path.localeCompare(b.path));
}
